import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "../contexts/GameContext";
import { TopBar } from "../components/game/TopBar";
import { StatsGrid } from "../components/game/StatsDisplay";
import { CHEST_RARITIES } from "../data/chests";
import { POTIONS } from "../data/shop";
import { CLASSES, STAT_LABELS, STAT_NAMES_FULL } from "../data/classes";
import { ITEMS } from "../data/items";
import { Coins, Gift, ShoppingCart, LogOut, Check, TrendingUp } from "lucide-react";
import { toast, Toaster } from "sonner";
import { SFX } from "../utils/audio";

const Shop = () => {
  const { currentEncounter, character, purchaseFromShop, leaveEncounter, bumpMeta } = useGame();
  const navigate = useNavigate();
  const [flashStat, setFlashStat] = useState(null);
  const boughtRef = useRef(false);

  useEffect(() => {
    if (!currentEncounter || currentEncounter.type !== "shop" || !character) {
      navigate("/map");
    }
  }, [currentEncounter, character, navigate]);

  if (!currentEncounter || currentEncounter.type !== "shop" || !character) return null;

  const { stock } = currentEncounter;
  const classe = CLASSES[character.classeId];

  // Toutes les compétences purchasable encore non-achetées
  const purchasableComps = (classe?.competences || []).filter((c) => c.purchasable);
  const owned = new Set(character.purchasedCompetences || []);
  const compsForSale = purchasableComps.filter((c) => !owned.has(c.id));

  const handleBuyCompetence = (comp) => {
    if (!comp) return;
    const ok = purchaseFromShop("competence", comp.id);
    if (ok !== false) {
      boughtRef.current = true;
      SFX.buy();
      toast.success(`${comp.nom} débloquée !`, {
        description: "Tu peux maintenant l'utiliser en combat.",
        icon: comp.icon,
        duration: 3500,
      });
    } else {
      toast.error("Achat impossible", { description: "Pas assez d'or" });
    }
  };

  const handleLeave = () => {
    if (!boughtRef.current) bumpMeta?.("shopsLeftEmpty", 1, { 10: "window_shopper" });
    leaveEncounter();
    navigate("/map");
  };

  const handleBuyPotion = (potion) => {
    const ok = purchaseFromShop("potion", potion.id);
    if (ok !== false) {
      boughtRef.current = true;
      SFX.buy();
      const statLabel = potion.stat === "pv" ? "PV max" : STAT_LABELS[potion.stat] || potion.stat.toUpperCase();
      toast.success(`+${potion.value} ${statLabel}`, {
        description: `${potion.nom} appliquée`,
        icon: potion.icon,
        duration: 2500,
      });
      // Flash the stat in the panel
      setFlashStat(potion.stat);
      setTimeout(() => setFlashStat(null), 1200);
    } else {
      toast.error("Achat impossible", { description: "Pas assez d'or" });
    }
  };

  const handleBuyConsumable = () => {
    const c = stock.consumable;
    if (!c) return;
    const ok = purchaseFromShop("consumable", c.id);
    if (ok !== false) {
      boughtRef.current = true;
      SFX.buy();
      toast.success(`${c.nom} ajoutée à l'inventaire !`, { icon: c.icon, duration: 2500 });
    } else {
      toast.error("Achat impossible", { description: "Pas assez d'or" });
    }
  };

  const handleBuyChest = () => {
    const goldBefore = character.gold;
    const ok = purchaseFromShop("chest", stock.chest.id);
    // Compute next snapshot post-render
    setTimeout(() => {
      if (ok === false) {
        toast.error("Achat impossible");
      } else {
        boughtRef.current = true;
        SFX.buy();
        toast.success("Coffre ouvert !", {
          description: "Tes stats ont été améliorées et tu as reçu de l'or.",
          icon: "🎁",
          duration: 3000,
        });
      }
    }, 50);
  };

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots flex flex-col overflow-hidden">
      <Toaster position="top-center" theme="dark" richColors />
      <TopBar />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 relative">
        <div className="absolute top-10 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <ShoppingCart className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
            <h2 className="font-display text-3xl font-bold">Boutique</h2>
            <p className="text-slate-300 text-sm mt-1">Achète des potions et un coffre mystère</p>
          </div>

          {/* Current stats panel — so player can see what they have */}
          <div data-testid="shop-stats-panel" className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 mb-5 cartoon-shadow-lg-static">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${classe.couleur} border-2 border-slate-950 overflow-hidden flex-shrink-0`}>
                  <img src={classe.image} alt={classe.nom} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-display font-bold text-sm">{classe.nom}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Stats actuelles</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-emerald-400 text-xs font-bold">
                <TrendingUp className="w-3 h-3" /> Tes stats
              </div>
            </div>
            <div className={flashStat ? `shop-flash-${flashStat}` : ""}>
              <StatsGrid stats={character.stats} compact data-testid="shop-current-stats" />
            </div>
          </div>

          {/* Potions */}
          <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">Potions permanentes (sélection aléatoire)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {stock.potions.map((potion, i) => {
              const canAfford = character.gold >= potion.price;
              return (
                <div
                  key={potion.id}
                  data-testid={`shop-potion-${potion.key}-${i}`}
                  className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static ${potion.sold ? "opacity-40" : ""}`}
                >
                  <div className={`${potion.couleur} border-2 border-slate-950 rounded-xl h-20 flex items-center justify-center text-4xl mb-2`}>
                    {potion.icon}
                  </div>
                  <h4 className="font-display font-bold text-sm leading-tight">{potion.nom}</h4>
                  <p className="text-xs text-emerald-400 font-bold mt-0.5">+{potion.value} {potion.stat === "pv" ? "PV max" : STAT_LABELS[potion.stat]}</p>
                  <p className="text-[10px] text-slate-400 mb-2">Permanent</p>
                  <button
                    data-testid={`buy-potion-${potion.id}`}
                    disabled={potion.sold || !canAfford}
                    onClick={() => handleBuyPotion(potion)}
                    className={`w-full ${potion.sold ? "bg-slate-700" : canAfford ? "bg-yellow-400 text-slate-950" : "bg-slate-700"} border-2 border-slate-950 rounded-xl py-1.5 font-display font-bold uppercase text-xs cartoon-shadow disabled:cursor-not-allowed disabled:hover:transform-none flex items-center justify-center gap-1`}
                  >
                    {potion.sold ? <><Check className="w-3 h-3" /> Vendu</> : <><Coins className="w-3 h-3" /> {potion.price}</>}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Consumable Health Potion (always present) */}
          {stock.consumable && (
            <>
              <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">Consommable (utilisable depuis l'inventaire)</h3>
              <div data-testid="shop-consumable" className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 mb-6 cartoon-shadow-lg-static flex items-center gap-3 max-w-md ${stock.consumable.sold ? "opacity-40" : ""}`}>
                <div className={`${stock.consumable.couleur} border-2 border-slate-950 rounded-xl w-16 h-16 flex items-center justify-center text-3xl flex-shrink-0`}>
                  {stock.consumable.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-bold text-sm">{stock.consumable.nom}</h4>
                  <p className="text-[11px] text-slate-300 leading-tight">{stock.consumable.description}</p>
                </div>
                <button
                  data-testid="buy-consumable-btn"
                  disabled={stock.consumable.sold || character.gold < stock.consumable.price}
                  onClick={handleBuyConsumable}
                  className={`${stock.consumable.sold ? "bg-slate-700" : character.gold >= stock.consumable.price ? "bg-yellow-400 text-slate-950" : "bg-slate-700"} border-2 border-slate-950 rounded-xl px-3 py-2 font-display font-bold text-xs cartoon-shadow disabled:cursor-not-allowed flex items-center gap-1`}
                >
                  {stock.consumable.sold ? <><Check className="w-3 h-3" /> Vendu</> : <><Coins className="w-3 h-3" /> {stock.consumable.price}</>}
                </button>
              </div>
            </>
          )}

          {/* Competences à débloquer */}
          {compsForSale.length > 0 && (
            <>
              <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">Compétences à débloquer ({compsForSale.length} dispo)</h3>
              <div data-testid="shop-competence-list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {compsForSale.map((comp) => (
                  <div key={comp.id} data-testid={`shop-comp-${comp.id}`} className="bg-slate-800 border-4 border-fuchsia-500 rounded-2xl p-3 cartoon-shadow-lg-static flex items-center gap-3">
                    <div className={`${comp.ultimate ? "bg-gradient-to-br from-rose-500 via-orange-500 to-yellow-400" : "bg-fuchsia-500"} border-2 border-slate-950 rounded-xl w-16 h-16 flex items-center justify-center text-3xl flex-shrink-0`}>
                      {comp.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-display font-bold text-sm">{comp.nom}{comp.ultimate ? " (ULTIME)" : ""}</h4>
                      <p className="text-[11px] text-slate-300 leading-tight">{comp.description}</p>
                    </div>
                    <button
                      data-testid={`buy-comp-btn-${comp.id}`}
                      disabled={character.gold < comp.purchaseCost}
                      onClick={() => handleBuyCompetence(comp)}
                      className={`${character.gold >= comp.purchaseCost ? "bg-yellow-400 text-slate-950" : "bg-slate-700"} border-2 border-slate-950 rounded-xl px-3 py-2 font-display font-bold text-xs cartoon-shadow disabled:cursor-not-allowed flex items-center gap-1`}
                    >
                      <Coins className="w-3 h-3" /> {comp.purchaseCost}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Chest */}
          <h3 className="font-display text-sm uppercase tracking-wider text-slate-400 mb-3">Coffre Mystère (1 par boutique)</h3>
          <div data-testid="shop-chest" className={`bg-slate-800 border-4 border-slate-950 rounded-3xl p-5 cartoon-shadow-lg-static max-w-md mb-6 ${stock.chest.sold ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-yellow-400 via-fuchsia-500 to-sky-500 border-4 border-slate-950 rounded-2xl w-24 h-24 flex items-center justify-center">
                <Gift className="w-12 h-12 text-white drop-shadow-[2px_2px_0_rgba(0,0,0,1)]" />
              </div>
              <div className="flex-1">
                <h4 className="font-display font-bold text-lg">Coffre Mystère</h4>
                <p className="text-sm text-slate-300">Rareté inconnue ! Plus on monte, meilleur ça devient.</p>
                {stock.chest.sold && stock.chest.openedRarity && (
                  <p className="text-xs mt-1 font-bold uppercase text-yellow-400">Tu as obtenu : {CHEST_RARITIES[stock.chest.openedRarity].nom}</p>
                )}
              </div>
            </div>
            {/* Détail des récompenses obtenues (comme les coffres de salle) */}
            {stock.chest.sold && stock.chest.openedStats && (
              <div data-testid="shop-chest-rewards" className="mt-3 bg-slate-900 border-2 border-yellow-400/40 rounded-2xl p-3 animate-slide-up">
                <h5 className="font-display font-bold text-xs mb-2 text-yellow-400 flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Récompenses obtenues</h5>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between bg-yellow-400/15 border-2 border-yellow-400 rounded-lg px-2.5 py-1.5">
                    <span className="flex items-center gap-1.5 font-bold text-xs"><Coins className="w-3.5 h-3.5 text-yellow-400" /> Or</span>
                    <span className="font-display font-bold text-yellow-400 text-sm">+{stock.chest.openedGold}</span>
                  </div>
                  {Object.entries(stock.chest.openedStats).map(([key, val]) => (
                    <div key={key} data-testid={`shop-chest-reward-${key}`} className="flex items-center justify-between bg-sky-400/15 border-2 border-sky-400 rounded-lg px-2.5 py-1.5">
                      <span className="font-bold text-xs">{STAT_NAMES_FULL[key] || key}</span>
                      <span className="font-display font-bold text-sky-400 text-sm">+{val}{(key === "crit" || key === "precision") ? "%" : ""}</span>
                    </div>
                  ))}
                  {stock.chest.openedItem && ITEMS[stock.chest.openedItem] && (
                    <div className="flex items-center justify-between bg-fuchsia-400/15 border-2 border-fuchsia-400 rounded-lg px-2.5 py-1.5">
                      <span className="flex items-center gap-1.5 font-bold text-xs"><span className="text-base">{ITEMS[stock.chest.openedItem].icon}</span> {ITEMS[stock.chest.openedItem].nom}</span>
                      <span className="font-display font-bold text-fuchsia-400 text-[10px]">Inventaire</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <button
              data-testid="buy-chest-btn"
              disabled={stock.chest.sold || character.gold < stock.chest.price}
              onClick={handleBuyChest}
              className={`w-full mt-3 ${stock.chest.sold ? "bg-slate-700" : character.gold >= stock.chest.price ? "bg-yellow-400 text-slate-950" : "bg-slate-700"} border-4 border-slate-950 rounded-2xl py-3 font-display font-bold uppercase cartoon-shadow disabled:cursor-not-allowed disabled:hover:transform-none flex items-center justify-center gap-2`}
            >
              {stock.chest.sold ? <><Check className="w-4 h-4" /> Acheté</> : <><Coins className="w-4 h-4" /> {stock.chest.price} or</>}
            </button>
          </div>

          <div className="text-center">
            <button
              data-testid="leave-shop-btn"
              onClick={handleLeave}
              className="bg-rose-500 text-white border-4 border-slate-950 rounded-2xl px-5 py-2 cartoon-shadow font-display font-bold uppercase tracking-wider flex items-center gap-2 mx-auto"
            >
              <LogOut className="w-4 h-4" /> Quitter la boutique
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
